"use client"

import Dialog from "@/base-components/Dialog"
import { useEffect, useMemo, useRef, useState } from "react"

type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>
  userChoice: Promise<{ outcome: "accepted" | "dismissed"; platform: string }>
}

type FooterInstallCopy = {
  mobile_app: string
  install_app: string
  installing_app: string
  app_installed: string
  install_unavailable: string
  install_manual_title: string
  install_manual_subtitle: string
  install_manual_close: string
  install_generic_instructions: string[]
  install_android_instructions: string[]
  install_chrome_desktop_instructions: string[]
  install_ios_instructions: string[]
  install_ios_other_browser_instructions: string[]
  install_mac_instructions: string[]
}

const isStandaloneMode = () => {
  if (typeof window === "undefined") return false

  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    (window.navigator as Navigator & { standalone?: boolean }).standalone ===
      true
  )
}

const getManualInstallMode = () => {
  if (typeof window === "undefined") return null

  const ua = window.navigator.userAgent
  const vendor = window.navigator.vendor || ""
  const maxTouchPoints = window.navigator.maxTouchPoints || 0
  const isDesktopClassIpad = /Macintosh/i.test(ua) && maxTouchPoints > 1
  const isIos = /iPhone|iPad|iPod/i.test(ua) || isDesktopClassIpad
  const isAndroid = /Android/i.test(ua)
  const isMac = /Macintosh/i.test(ua) && !isDesktopClassIpad
  const isWindows = /Windows/i.test(ua)
  const isLinux = /Linux/i.test(ua) && !isAndroid
  const isChromeFamily =
    /Chrome|Chromium|CriOS|Edg|EdgiOS/i.test(ua) && !/Firefox|FxiOS/i.test(ua)
  const isAppleVendor = vendor.includes("Apple")
  const isSafari =
    isAppleVendor && !/CriOS|FxiOS|EdgiOS|Chrome|Chromium|Android/i.test(ua)

  if (isSafari && isIos) return "ios"
  if (!isSafari && isIos) return "ios-other-browser"
  if (isSafari && isMac) return "mac"
  if (isChromeFamily && isAndroid) return "android"
  if (isChromeFamily && (isMac || isWindows || isLinux)) return "chrome-desktop"

  return null
}

const PwaInstallSection = ({ copy }: { copy: FooterInstallCopy }) => {
  const [deferredPrompt, setDeferredPrompt] =
    useState<BeforeInstallPromptEvent | null>(null)
  const [isInstalled, setIsInstalled] = useState(false)
  const [isInstalling, setIsInstalling] = useState(false)
  const [isInstallGuideOpen, setIsInstallGuideOpen] = useState(false)
  const deferredPromptRef = useRef<BeforeInstallPromptEvent | null>(null)
  const [manualInstallMode, setManualInstallMode] = useState<string | null>(
    null,
  )

  useEffect(() => {
    setManualInstallMode(getManualInstallMode())
  }, [])
  useEffect(() => {
    setIsInstalled(isStandaloneMode())

    const handleBeforeInstallPrompt = (event: Event) => {
      const installEvent = event as BeforeInstallPromptEvent
      installEvent.preventDefault()
      deferredPromptRef.current = installEvent
      setDeferredPrompt(installEvent)
    }

    const handleAppInstalled = () => {
      setIsInstalled(true)
      deferredPromptRef.current = null
      setDeferredPrompt(null)
    }

    window.addEventListener(
      "beforeinstallprompt",
      handleBeforeInstallPrompt as EventListener,
    )
    window.addEventListener("appinstalled", handleAppInstalled)

    return () => {
      window.removeEventListener(
        "beforeinstallprompt",
        handleBeforeInstallPrompt as EventListener,
      )
      window.removeEventListener("appinstalled", handleAppInstalled)
    }
  }, [])

  const buttonLabel = useMemo(() => {
    if (isInstalled) return copy.app_installed
    if (isInstalling) return copy.installing_app
    return copy.install_app
  }, [
    copy.app_installed,
    copy.install_app,
    copy.installing_app,
    isInstalled,
    isInstalling,
  ])

  const handleInstall = async () => {
    if (isInstalled || isInstalling) return

    setIsInstalling(true)

    try {
      if (!deferredPromptRef.current) {
        await new Promise(resolve => window.setTimeout(resolve, 1200))
      }

      const promptEvent = deferredPromptRef.current

      if (!promptEvent) {
        setIsInstallGuideOpen(true)
        return
      }

      await promptEvent.prompt()
      const choice = await promptEvent.userChoice

      if (choice.outcome === "accepted") {
        setIsInstalled(true)
      }
    } finally {
      deferredPromptRef.current = null
      setDeferredPrompt(null)
      setIsInstalling(false)
    }
  }

  const isButtonDisabled = isInstalled || isInstalling

  const buttonTitle = useMemo(() => {
    if (isInstalled) return copy.app_installed
    if (deferredPrompt) return copy.install_app
    if (manualInstallMode) return copy.install_manual_title
    return copy.install_unavailable
  }, [
    copy.app_installed,
    copy.install_app,
    copy.install_manual_title,
    copy.install_unavailable,
    manualInstallMode,
    deferredPrompt,
    isInstalled,
  ])

  const installGuideSteps = useMemo(() => {
    switch (manualInstallMode) {
      case "ios":
        return copy.install_ios_instructions
      case "ios-other-browser":
        return copy.install_ios_other_browser_instructions
      case "mac":
        return copy.install_mac_instructions
      case "android":
        return copy.install_android_instructions
      case "chrome-desktop":
        return copy.install_chrome_desktop_instructions
      default:
        return copy.install_generic_instructions
    }
  }, [
    copy.install_generic_instructions,
    copy.install_android_instructions,
    copy.install_chrome_desktop_instructions,
    copy.install_ios_instructions,
    copy.install_ios_other_browser_instructions,
    copy.install_mac_instructions,
    manualInstallMode,
  ])

  return (
    <div>
      <h5 className="mb-5 font-semibold text-white">{copy.mobile_app}</h5>

      <button
        type="button"
        onClick={handleInstall}
        disabled={isButtonDisabled}
        title={buttonTitle}
        className="hero-button-gradient inline-flex min-h-[44px] items-center justify-center rounded-lg px-5 py-3 text-sm font-medium text-white transition duration-200 hover:opacity-80 disabled:cursor-default disabled:opacity-60"
      >
        {buttonLabel}
      </button>

      <Dialog
        open={isInstallGuideOpen}
        onClose={() => setIsInstallGuideOpen(false)}
        size="md"
      >
        <Dialog.Panel className="mx-auto mt-[10%] w-[92%] max-w-lg rounded-xl border border-white/[0.12] bg-dark-8 p-0 text-white">
          <Dialog.Title className="border-white/[0.12] bg-transparent px-6 py-5 text-lg font-semibold text-white">
            {copy.install_manual_title}
          </Dialog.Title>
          <Dialog.Description className="px-6 py-5">
            <p className="text-sm leading-6 text-slate-300">
              {copy.install_manual_subtitle}
            </p>
            <ol className="mt-5 space-y-3">
              {installGuideSteps.map((step, index) => (
                <li
                  key={`${manualInstallMode ?? "manual"}-${index}`}
                  className="flex items-start gap-3 rounded-lg border border-white/[0.1] bg-white/[0.03] px-4 py-3"
                >
                  <span className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-purple text-sm font-semibold text-white">
                    {index + 1}
                  </span>
                  <span className="pt-0.5 text-sm leading-6 text-slate-200">
                    {step}
                  </span>
                </li>
              ))}
            </ol>
          </Dialog.Description>
          <Dialog.Footer className="border-white/[0.12] px-6 py-4">
            <button
              type="button"
              onClick={() => setIsInstallGuideOpen(false)}
              className="hero-button-gradient inline-flex min-h-[44px] items-center justify-center rounded-lg px-5 py-3 text-sm font-medium text-white transition duration-200 hover:opacity-80"
            >
              {copy.install_manual_close}
            </button>
          </Dialog.Footer>
        </Dialog.Panel>
      </Dialog>
    </div>
  )
}

export default PwaInstallSection
